"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.concessionUsageQuerySchema = exports.simulateConcessionSchema = exports.separateAppointmentSchema = exports.cancelScheduledConcessionRuleSchema = exports.switchOnConcessionRuleSchema = exports.switchConcessionTypeSchema = exports.updateConcessionTypeSchema = exports.createConcessionTypeSchema = exports.saveConcessionRuleSchema = exports.excludeConcessionSchema = exports.switchOffConcessionRuleSchema = exports.reviseConcessionRuleSchema = exports.createConcessionRuleSchema = void 0;
const zod_1 = require("zod");
const concession_policy_1 = require("../domain/concession-policy");
const discount_rule_scope_1 = require("../domain/discount-rule-scope");
/**
 * The Concession policy page's requests (owner, 2026-10-17). Every change carries a REASON - a rate is a management
 * decision with a cost, and "why" is the first thing asked at month end - and a calendar DAY (India) rather than a
 * timestamp, because "from 1 November" is how a rate change is spoken.
 */
const calendarDay = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date in the form YYYY-MM-DD');
const reason = zod_1.z
    .string()
    .trim()
    .min(5, 'Give a reason (at least 5 characters) - it is kept in the history')
    .max(500);
const methodEnum = zod_1.z.enum(['PERCENTAGE', 'FLAT_AMOUNT']);
/** A concession category: one of the built-in five or an administrator-added type. The server refuses a code that is neither. */
const categoryCode = zod_1.z.string().regex(discount_rule_scope_1.CONCESSION_TYPE_CODE_PATTERN, 'Not a concession type code');
/** A percentage is a whole number 1-100; a flat amount is paise (the page takes rupees and converts). */
function validateAmount(value, ctx) {
    if (value.method === 'PERCENTAGE' && value.value !== undefined && value.value > 100) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['value'], message: 'A percentage cannot be more than 100' });
    }
}
exports.createConcessionRuleSchema = zod_1.z
    .object({
    section: zod_1.z.enum(concession_policy_1.CONCESSION_SECTIONS),
    /** null = every category. */
    category: categoryCode.nullable(),
    method: methodEnum,
    value: zod_1.z.number().int().positive(),
    /** Rupee cap on one line, in paise. A percentage only. */
    capPaise: zod_1.z.number().int().positive().nullable().optional(),
    effectiveFrom: calendarDay,
    effectiveTo: calendarDay.nullable().optional(),
    reason,
})
    .superRefine((input, ctx) => {
    validateAmount(input, ctx);
    if (input.capPaise != null && input.method !== 'PERCENTAGE') {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['capPaise'], message: 'A cap applies to a percentage only' });
    }
    if (input.effectiveTo && input.effectiveTo < input.effectiveFrom) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['effectiveTo'], message: 'The end date is before the start date' });
    }
});
exports.reviseConcessionRuleSchema = zod_1.z
    .object({
    method: methodEnum.optional(),
    value: zod_1.z.number().int().positive().optional(),
    /** null clears the cap. */
    capPaise: zod_1.z.number().int().positive().nullable().optional(),
    effectiveFrom: calendarDay,
    reason,
})
    .superRefine((input, ctx) => validateAmount(input, ctx));
exports.switchOffConcessionRuleSchema = zod_1.z.object({
    /** Omitted = now. A future day = the rule keeps running until the day before it. */
    from: calendarDay.optional(),
    reason,
});
/**
 * "No concession for this category here" - a rule at rate 0 that wins its place, so the all-categories rule beneath it is
 * not reached. The honest way to take one category out of a rule that covers several (switching the named rule off would
 * only hand the category the all-categories rate).
 */
exports.excludeConcessionSchema = zod_1.z
    .object({
    section: zod_1.z.enum(concession_policy_1.CONCESSION_SECTIONS),
    category: categoryCode,
    effectiveFrom: calendarDay,
    effectiveTo: calendarDay.nullable().optional(),
    reason,
})
    .superRefine((input, ctx) => {
    if (input.effectiveTo && input.effectiveTo < input.effectiveFrom) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['effectiveTo'], message: 'The end date is before the start date' });
    }
});
/**
 * THE RULE FORM (add a rule / update a rule, 2026-10-17): who it is for, what it covers, how much, and from when. One shape
 * for both - an update sends the whole form again, with a reason.
 */
exports.saveConcessionRuleSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(3, 'Give the rule a name').max(255),
    /** null = every category. */
    category: categoryCode.nullable(),
    scope: zod_1.z.enum(discount_rule_scope_1.RULE_SCOPES),
    /** The services, by code - for Service Code and the "Service Code + Lab / Radiology" choices. */
    serviceCodes: zod_1.z.array(zod_1.z.string().trim().toUpperCase().regex(discount_rule_scope_1.SERVICE_CODE_PATTERN, 'Not a service code')).max(discount_rule_scope_1.SERVICE_CODES_MAX).default([]),
    /** The account heads - for the Account head choice. */
    accountHeadIds: zod_1.z.array(zod_1.z.string().uuid()).max(discount_rule_scope_1.ACCOUNT_HEADS_MAX).default([]),
    method: methodEnum,
    /** Whole percent 1-100, or PAISE for a flat amount (the form takes rupees and converts). */
    value: zod_1.z.number().int().positive(),
    /** Rupee cap on one line, in paise. A percentage only. */
    capPaise: zod_1.z.number().int().positive().nullable().optional(),
    priority: zod_1.z.number().int().min(0).max(1000).optional(),
    effectiveFrom: calendarDay,
    effectiveTo: calendarDay.nullable().optional(),
    reason,
})
    .superRefine((input, ctx) => {
    validateAmount(input, ctx);
    if (input.capPaise != null && input.method !== 'PERCENTAGE') {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['capPaise'], message: 'A cap applies to a percentage only' });
    }
    if (input.effectiveTo && input.effectiveTo < input.effectiveFrom) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['effectiveTo'], message: 'The end date is before the start date' });
    }
    if ((0, discount_rule_scope_1.scopeNeedsServiceCodes)(input.scope) && input.serviceCodes.length === 0) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['serviceCodes'], message: 'Name at least one service code' });
    }
    if (!(0, discount_rule_scope_1.scopeNeedsServiceCodes)(input.scope) && input.serviceCodes.length > 0) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['serviceCodes'], message: 'Service codes are only for the Service Code choices' });
    }
    if ((0, discount_rule_scope_1.scopeNeedsAccountHeads)(input.scope) && input.accountHeadIds.length === 0) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['accountHeadIds'], message: 'Pick at least one account head' });
    }
    if (!(0, discount_rule_scope_1.scopeNeedsAccountHeads)(input.scope) && input.accountHeadIds.length > 0) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['accountHeadIds'], message: 'Account heads are only for the Account head choice' });
    }
    /* A Retired staff rule may be written for ANY scope: Retired staff get nothing but the consultation until an administrator
       names them (the confirmed rates table, 2026-10-17: "nothing until admin adds rules"). */
});
/** Add a concession type ("anything can come"): a name; the code is made from it. */
exports.createConcessionTypeSchema = zod_1.z.object({
    label: zod_1.z.string().trim().min(2, 'Give the concession a name').max(60),
    reason,
});
exports.updateConcessionTypeSchema = zod_1.z.object({
    label: zod_1.z.string().trim().min(2).max(60),
    reason,
});
exports.switchConcessionTypeSchema = zod_1.z.object({ reason });
exports.switchOnConcessionRuleSchema = zod_1.z.object({ reason });
exports.cancelScheduledConcessionRuleSchema = zod_1.z.object({ reason });
exports.separateAppointmentSchema = zod_1.z.object({ reason });
exports.simulateConcessionSchema = zod_1.z.object({
    category: categoryCode,
    section: zod_1.z.enum(concession_policy_1.CONCESSION_SECTIONS),
    grossPaise: zod_1.z.number().int().positive().max(100_000_000),
});
exports.concessionUsageQuerySchema = zod_1.z.object({
    days: zod_1.z.coerce.number().int().min(1).max(365).default(30),
});
//# sourceMappingURL=concession-policy.schema.js.map